import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router';
import { track } from '@amplitude/analytics-browser';
import { Box, Button } from '@nimbus-ds/components';
import { useQueryClient } from '@tanstack/react-query';
import AppLayout from 'App/components/AppLayout';
import { ROUTES } from 'App/Routes/constants';
import {
  useBankAccount,
  useIdentityValidation,
  useProofOfLifeUrl,
  useStoreInfo,
} from 'commons/hooks';
import { GetIdValidationResponse } from 'commons/services';
import { KycFlagName, KycFlagType } from 'commons/types';
import { getLegalEntityType, useTranslationWithPrefix } from 'commons/utils';
import {
  dataDogLogger,
  DataDogLoggerActionTypes,
} from 'commons/utils/dataDogLogger';
import { config } from 'config';
import { useIdValidationRoutes } from '../hooks';
import { useAipriseVerification } from './aiprise/useAipriseVerification';
import ProofOfLifeContent from './ProofOfLifeContent';
import ProofOfLifeInfoSkeleton from './ProofOfLifeInfoSkeleton';
import { verifyIfKycFlagIsEnabled } from './verifyKycFlag';

function extractVerificationSessionId(url?: string): string | undefined {
  if (!url) return undefined;
  try {
    return (
      new URL(url).searchParams.get('verification_session_id') ?? undefined
    );
  } catch {
    return undefined;
  }
}

function ProofOfLifeAiprise(): JSX.Element {
  const { t } = useTranslationWithPrefix('identityValidation.proofOfLife');
  const { t: tButtons } = useTranslationWithPrefix('identityValidation');

  const { goBack, push } = useHistory();

  const [isValidationChecked, setIsValidationChecked] = useState(false);

  const { goToNextRoute } = useIdValidationRoutes();
  const queryClient = useQueryClient();
  const { storeInfo, setStoreInfo } = useStoreInfo();
  const idValidation = useIdentityValidation();
  const bankAccount = useBankAccount();
  const fiscalDocument = bankAccount.data?.fiscalDocument;
  const legalEntityType = getLegalEntityType(fiscalDocument);
  const { proofOfLifeUrl, isLoading: proofOfLifeUrlLoading } =
    useProofOfLifeUrl();

  const setKycStatusToPendingReview = useCallback(async () => {
    const queryKey = ['identityValidation'];
    await queryClient.cancelQueries({ queryKey });
    queryClient.setQueryData<GetIdValidationResponse>(queryKey, (old) => ({
      ...old,
      status: 'pending_review',
      updatedAt: new Date().toISOString(),
      kycDocumentErrors: [],
    }));
    queryClient.setQueryDefaults(queryKey, { staleTime: Infinity });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    queryClient.cancelQueries,
    queryClient.setQueryData,
    queryClient.setQueryDefaults,
  ]);

  const areAllKycFlagsEnabled = (kycFlags: KycFlagType[]) => {
    return kycFlags.every((flag) => flag.enabled);
  };

  const enableIdentityValidationFlag = useCallback(async () => {
    const updatedStoreInfo = {
      ...storeInfo,
      kycFlags: storeInfo.kycFlags.map((flag) =>
        flag.name === KycFlagName.IdentityValidation
          ? { ...flag, enabled: true }
          : flag,
      ),
    };
    setStoreInfo(updatedStoreInfo);

    if (areAllKycFlagsEnabled(updatedStoreInfo.kycFlags)) {
      await setKycStatusToPendingReview();
    }
  }, [storeInfo, setStoreInfo, setKycStatusToPendingReview]);

  const finishVerification = useCallback(async () => {
    track('pago-nube-prueba-de-vida-analisis', {
      legal_entity_type: legalEntityType,
    });
    try {
      await enableIdentityValidationFlag();
    } catch (error) {
      dataDogLogger.generate({
        actionMessage: 'Failed to finalise AiPrise proof of life',
        actionOwner: 'ProofOfLifeAipriseIntegration',
        actionType: DataDogLoggerActionTypes.Error,
        actionData: {
          error: error instanceof Error ? error.message : String(error),
        },
      });
    } finally {
      goToNextRoute();
    }
  }, [enableIdentityValidationFlag, goToNextRoute, legalEntityType]);

  const exitVerification = useCallback(() => {
    goToNextRoute();
  }, [goToNextRoute]);

  const { sdkReady, sdkError, elementRef } = useAipriseVerification({
    onFinish: finishVerification,
    onExit: exitVerification,
    legalEntityType,
  });

  useEffect(() => {
    if (sdkError) exitVerification();
  }, [sdkError, exitVerification]);

  // The AiPrise SDK disables shadow DOM, so its inner button is queryable
  // directly. We keep a ref to the `<aiprise-button>` host element so we
  // can locate and click the inner button from the Nimbus action button.
  const aipriseButtonRef = useRef<HTMLElement | null>(null);
  const setAipriseButtonRef = useCallback(
    (node: HTMLElement | null) => {
      aipriseButtonRef.current = node;
      elementRef(node);
    },
    [elementRef],
  );

  const handleWucPreCheck = useCallback(async (): Promise<boolean> => {
    if (idValidation.data?.status === 'pending') {
      const isProofOfLifeAlreadyDone = await verifyIfKycFlagIsEnabled(
        KycFlagName.IdentityValidation,
      );
      if (isProofOfLifeAlreadyDone) {
        push(ROUTES.IDENTITY_VALIDATION_STEPS, { isProofOfLifeAlreadyDone });
        return true;
      }
    }
    return false;
  }, [idValidation.data?.status, push]);

  const handleStartVerification = useCallback(async () => {
    const alreadyDone = await handleWucPreCheck();
    if (alreadyDone) return;
    track('pago-nube-prueba-de-vida-recomendaciones', {
      legal_entity_type: legalEntityType,
    });

    const innerButton =
      aipriseButtonRef.current?.querySelector<HTMLButtonElement>(
        'button.aiprise-button',
      );
    innerButton?.click();
  }, [handleWucPreCheck, legalEntityType]);

  const verificationSessionId = extractVerificationSessionId(proofOfLifeUrl);

  const canStartVerification =
    isValidationChecked && sdkReady && Boolean(verificationSessionId);

  const footerButtons = (
    <Box gap="4" display="flex" flexDirection="row">
      <Button onClick={goBack}>{tButtons('buttons.cancel')}</Button>
      <Button
        appearance="primary"
        disabled={!canStartVerification}
        onClick={handleStartVerification}
      >
        {tButtons('buttons.continue')}
      </Button>
      {canStartVerification && (
        <aiprise-button
          ref={setAipriseButtonRef}
          template-id={config.aiprise.templateId}
          mode={config.aiprise.mode}
          session-id={verificationSessionId}
          style={{ display: 'none' }}
        />
      )}
    </Box>
  );

  if (!sdkReady || proofOfLifeUrlLoading) {
    return <ProofOfLifeInfoSkeleton />;
  }

  return (
    <AppLayout title={t('title')} footer={footerButtons}>
      <ProofOfLifeContent
        isValidationChecked={isValidationChecked}
        onCheckboxChange={setIsValidationChecked}
      />
    </AppLayout>
  );
}

export default ProofOfLifeAiprise;
